import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ImageUpload } from './ImageUpload';
import { useAuth } from '../../contexts/AuthContext';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const normalizeImagesForInput = (imgs) => {
  if (!imgs) return [];
  if (Array.isArray(imgs)) {
    return imgs
      .map((x) => {
        if (!x) return '';
        if (typeof x === 'string') return x.trim();
        if (typeof x === 'object') return String(x.url || x.src || '').trim();
        return String(x).trim();
      })
      .filter(Boolean);
  }
  if (typeof imgs === 'string') {
    const s = imgs.trim();
    try {
      if (s.startsWith('[') || s.startsWith('{')) {
        const parsed = JSON.parse(s);
        return normalizeImagesForInput(parsed);
      }
    } catch (_) {}
    return s.split(',').map((x) => x.trim()).filter(Boolean);
  }
  if (typeof imgs === 'object') {
    const u = imgs.url || imgs.src;
    return u ? [String(u).trim()] : [];
  }
  return [];
};


const normalizeImagesLocal = (images) => {
  const asUrl = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'object') {
      if (typeof v.url === 'string') return v.url.trim();
      if (typeof v.src === 'string') return v.src.trim();
    }
    return '';
  };

  if (Array.isArray(images)) return images.map(asUrl).filter(Boolean);
  if (typeof images === 'string') {
    const s = images.trim();
    if (!s) return [];
    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed.map(asUrl).filter(Boolean);
      } catch {}
    }
    return s.split(',').map((x) => x.trim()).filter(Boolean);
  }
  if (typeof images === 'object' && images) {
    const u = asUrl(images);
    return u ? [u] : [];
  }
  return [];
};


export const AdminProducts = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    description: '',
    ingredients: '',
    usage_instructions: '',
    warnings: '',
    price: '',
    discount: '0',
    stock_quantity: '',
    category_id: '',
    subcategory_id: '',
    images: '',
    status: 'active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/products?status=`),
        axios.get(`${API_URL}/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.category_id) {
      fetchSubcategories(formData.category_id);
    }
  }, [formData.category_id]);

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await axios.get(`${API_URL}/subcategories?category_id=${categoryId}`);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Failed to load subcategories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      
const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const toInt = (v, fallback = 0) => {
      const n = Number.parseInt(String(v ?? '').trim(), 10);
      return Number.isFinite(n) ? n : fallback;
    };
    const toFloat = (v, fallback = 0) => {
      const n = Number.parseFloat(String(v ?? '').trim());
      return Number.isFinite(n) ? n : fallback;
    };
    const normalizeImagesAny = (imgs) => {
      if (!imgs) return [];
      if (Array.isArray(imgs)) {
        return imgs.map((x) => (typeof x === 'string' ? x.trim() : (x?.url || x?.src || ''))).filter(Boolean);
      }
      if (typeof imgs === 'string') {
        const s = imgs.trim();
        try {
          if (s.startsWith('[') || s.startsWith('{')) {
            const parsed = JSON.parse(s);
            return normalizeImagesAny(parsed);
          }
        } catch (e) {}
        return s.split(',').map((x) => x.trim()).filter(Boolean);
      }
      if (typeof imgs === 'object') {
        const u = imgs.url || imgs.src;
        return u ? [String(u).trim()] : [];
      }
      return [];
    };

    const productData = {
      ...formData,
      price: toFloat(formData.price, 0),
      discount: Math.min(100, Math.max(0, toInt(formData.discount, 0))),
      stock_quantity: Math.max(0, toInt(formData.stock_quantity, 0)),
      images: normalizeImagesAny(formData.images),
      subcategory_id: formData.subcategory_id || null,
      category_id: formData.category_id || null,
    };

    if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/products`, productData, {
          headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
        });
        toast.success('Product created successfully');
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    const imagesArr = Array.isArray(product.images)
      ? product.images
      : (typeof product.images === 'string'
          ? product.images.split(',').map((s) => s.trim()).filter(Boolean)
          : []);

    setEditingProduct(product);
    setFormData({
      name: product.name,
      brand: product.brand,
      description: product.description,
      ingredients: product.ingredients || '',
      usage_instructions: product.usage_instructions || '',
      warnings: product.warnings || '',
      price: product.price.toString(),
      discount: product.discount.toString(),
      stock_quantity: product.stock_quantity.toString(),
      category_id: product.category_id,
      subcategory_id: product.subcategory_id || '',
      images: imagesArr.join(', '),
      status: product.status
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API_URL}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
      });
      toast.success('Product deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      description: '',
      ingredients: '',
      usage_instructions: '',
      warnings: '',
      price: '',
      discount: '0',
      stock_quantity: '',
      category_id: '',
      subcategory_id: '',
      images: '',
      status: 'active'
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.brand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-3xl font-bold">Product Management</h2>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="brand">Brand *</Label>
                  <Input
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value, subcategory_id: '' })}
                    className="flex h-11 w-full rounded-lg border border-input bg-background px-4 py-2 text-sm"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="subcategory">Subcategory</Label>
                  <select
                    id="subcategory"
                    value={formData.subcategory_id}
                    onChange={(e) => setFormData({ ...formData, subcategory_id: e.target.value })}
                    className="flex h-11 w-full rounded-lg border border-input bg-background px-4 py-2 text-sm"
                    disabled={!formData.category_id}
                  >
                    <option value="">No subcategory</option>
                    {subcategories.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Price (€) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="discount">Discount (%)</Label>
                  <Input
                    id="discount"
                    type="number"
                    step="1"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="stock">Stock Quantity *</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="ingredients">Ingredients</Label>
                <Textarea
                  id="ingredients"
                  value={formData.ingredients}
                  onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="usage">Usage Instructions</Label>
                <Textarea
                  id="usage"
                  value={formData.usage_instructions}
                  onChange={(e) => setFormData({ ...formData, usage_instructions: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="warnings">Warnings</Label>
                <Textarea
                  id="warnings"
                  value={formData.warnings}
                  onChange={(e) => setFormData({ ...formData, warnings: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <ImageUpload
                  token={token}
                  existingImages={normalizeImagesForInput(formData.images)}
                  onUploadComplete={(images) => {
                    setFormData({ ...formData, images: images.join(', ') });
                  }}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Upload images or use the manual URL input below for external images
                </p>
              </div>

              <div>
                <Label htmlFor="images">Or paste image URLs (comma-separated)</Label>
                <Input
                  id="images"
                  value={formData.images}
                  onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                  placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                />
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="flex h-11 w-full rounded-lg border border-input bg-background px-4 py-2 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold">Product</th>
                  <th className="text-left p-3 font-semibold">Brand</th>
                  <th className="text-left p-3 font-semibold">Price</th>
                  <th className="text-left p-3 font-semibold">Stock</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="border-b hover:bg-muted/50">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {(() => {
                          const imgs = Array.isArray(product.images)
                            ? product.images
                            : (typeof product.images === 'string'
                                ? product.images.split(',').map((s) => s.trim()).filter(Boolean)
                                : []);
                          const first = imgs[0];
                          return first ? (
                            <img
                              src={first}
                              alt={product.name}
                              className="w-12 h-12 rounded object-cover"
                            />
                          ) : null;
                        })()}
                        <span className="font-medium">{product.name}</span>
                      </div>
                    </td>
                    <td className="p-3">{product.brand}</td>
                    <td className="p-3">
                      <div>
                        <span className="font-semibold">{formatPrice(product.price)}</span>
                        {product.discount > 0 && (
                          <Badge variant="destructive" className="ml-2">-{product.discount}%</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant={product.stock_quantity > 0 ? 'success' : 'destructive'}>
                        {product.stock_quantity}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Badge variant={product.status === 'active' ? 'default' : 'secondary'}>
                        {product.status}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(product)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};